const DEFAULT_MODEL_LOOKUP_ATTEMPTS = 3;
const DEFAULT_MODEL_LOOKUP_RETRY_MS = 500;

/**
 * Remove stale or duplicate model IDs from a persisted/user-provided selection.
 * Falls back to the current defaults when no supported selection remains.
 */
export function normalizeSelectedImageModelIds({
  selectedModelIds,
  availableModelIds,
  fallbackModelIds
}) {
  const availableIds = availableModelIds instanceof Set
    ? availableModelIds
    : new Set(Array.isArray(availableModelIds) ? availableModelIds : []);
  const supportedIds = Array.isArray(selectedModelIds)
    ? [...new Set(selectedModelIds.filter(modelId => availableIds.has(modelId)))]
    : [];

  if (supportedIds.length > 0) {
    return supportedIds;
  }

  return Array.isArray(fallbackModelIds)
    ? [...new Set(fallbackModelIds.filter(modelId => availableIds.has(modelId)))]
    : [];
}

function wait(ms) {
  return new Promise(resolve => setTimeout(resolve, ms));
}

function createSelectionError(message, code, requestedModelIds, missingModelIds, cause) {
  const error = new Error(message);
  error.code = code;
  error.requestedModelIds = requestedModelIds;
  error.missingModelIds = missingModelIds;
  if (cause) error.cause = cause;
  return error;
}

/**
 * Resolve every requested image model from one catalog snapshot.
 *
 * Generation must fail closed when a selected model is unavailable. Choosing
 * the first or most popular catalog entry can silently submit an unrelated
 * (and potentially differently billed) model.
 */
export async function loadExactImageModels({
  loadModels,
  requestedModelIds,
  attempts = DEFAULT_MODEL_LOOKUP_ATTEMPTS,
  retryDelayMs = DEFAULT_MODEL_LOOKUP_RETRY_MS,
  sleep = wait,
  onRetry
}) {
  if (typeof loadModels !== 'function') {
    throw new TypeError('loadModels must be a function');
  }

  const requested = Array.isArray(requestedModelIds)
    ? requestedModelIds.filter(id => typeof id === 'string' && id.length > 0)
    : [];
  if (requested.length === 0) {
    throw new Error('At least one image model must be selected.');
  }

  const maxAttempts = Math.max(1, Number.parseInt(attempts, 10) || DEFAULT_MODEL_LOOKUP_ATTEMPTS);
  const uniqueRequested = [...new Set(requested)];
  let missingModelIds = uniqueRequested;
  let lastCatalogError = null;
  let receivedCatalog = false;

  for (let attempt = 1; attempt <= maxAttempts; attempt++) {
    try {
      const catalog = await loadModels();
      receivedCatalog = true;
      lastCatalogError = null;

      const modelsById = new Map(
        (Array.isArray(catalog) ? catalog : [])
          .filter(model => model?.id)
          .map(model => [model.id, model])
      );
      missingModelIds = uniqueRequested.filter(id => !modelsById.has(id));

      if (missingModelIds.length === 0) {
        return requested.map(id => modelsById.get(id));
      }
    } catch (error) {
      lastCatalogError = error;
    }

    if (attempt < maxAttempts) {
      onRetry?.({
        attempt,
        nextAttempt: attempt + 1,
        maxAttempts,
        missingModelIds: [...missingModelIds],
        error: lastCatalogError
      });
      await sleep(retryDelayMs);
    }
  }

  if (!receivedCatalog) {
    throw createSelectionError(
      'The model catalog is temporarily unavailable. No render was submitted. Please retry in a few seconds.',
      'MODEL_CATALOG_UNAVAILABLE',
      uniqueRequested,
      uniqueRequested,
      lastCatalogError
    );
  }

  const modelList = missingModelIds.map(id => `"${id}"`).join(', ');
  const noun = missingModelIds.length === 1 ? 'model is' : 'models are';
  throw createSelectionError(
    `The selected ${noun} temporarily unavailable: ${modelList}. No render was submitted. Please retry in a few seconds or choose another model.`,
    'SELECTED_MODEL_UNAVAILABLE',
    uniqueRequested,
    missingModelIds
  );
}
